import { Tooltip, useMantineTheme } from '@mantine/core';
import {
  IconFile,
  IconFileTypeDocx,
  IconFileTypeHtml,
  IconFileTypePdf,
  IconFileTypePpt,
  IconFileTypeXls,
  IconPhoto,
} from '@tabler/icons-react';
import React from 'react';

import { Http } from '@mygpt/app/http';
import { useChatStore } from '@mygpt/app/chat/stores/useChatStore';
import { useFilePreviewStore } from '@mygpt/app/chat/stores/useFilePreviewStore';
import { useFileViewerStore } from '@mygpt/app/chat/stores/useFileViewerStore';
import { pickReadableAccent } from '@mygpt/app/chat/utils/colorContrast';
import { openFilePreviewTab } from '@mygpt/app/chat/utils/filePreviewHandoff';
import { FileChipKind, parseFilePreviewLink, resolveFileChipKind } from '@mygpt/app/chat/utils/filePreviewLink';
import { Footnote } from '@mygpt/types';

import classes from './fileChip.module.css';

/**
 * 回答正文里的知识库文件卡片。
 *
 * 由 Markdown.tsx 的 a 渲染拦截 /preview/<file_id> 前缀链接后交给本组件（解析见 utils/filePreviewLink）。
 * 它和 <CITATION> 角标是两条并存的链路：角标是 RAG 命中片段，卡片是模型主动提到的文件。
 *
 * 点击后打开 FileViewer（useFileViewerStore）：浏览器本地解析原文件的纯预览，
 * pdf / docx / pptx / xlsx / csv / md / 图片都在面板里看，翻到链接指定的页，不请求高亮（高亮是角标那条链路的事）。
 * html 另开标签页。小部件内（iframe 太窄）在新标签页打开 /preview/:fileId，文件名经 postMessage 握手交接。
 */

const KIND_ICON: Record<FileChipKind, React.ComponentType<{ size?: number | string }>> = {
  pdf: IconFileTypePdf,
  word: IconFileTypeDocx,
  ppt: IconFileTypePpt,
  excel: IconFileTypeXls,
  image: IconPhoto,
  web: IconFileTypeHtml,
  file: IconFile,
};

interface FileChipProps {
  href: string;
  /** Markdown 链接的 title 位，后端约定放文件名 */
  title?: string;
  /** 链接文字 */
  children?: React.ReactNode;
  /** 本条回答的 reference_list，用于没有文件名时按 file_id 反查 */
  referenceList?: Footnote[];
  /** 小部件自定义的品牌主色（widgetColor.chatColor） */
  accentColor?: string;
  /** 小部件自定义的正文文字色（widgetColor.botMessageTextColor） */
  textColor?: string;
  /** 小部件自定义的回答气泡色（widgetColor.botMessageColor），用来判断强调色在上面是否看得清 */
  bubbleColor?: string;
  /** 数字人场景：正文铺在视频上，白字 + 半透明白底 */
  isDigitalHuman?: boolean;
}

/**
 * 按场景算出卡片的配色变量；返回空对象时走 CSS 里的 Mantine 主题主色默认值。
 * color-mix 用于把任意强调色打淡做底色，Chrome 111+ / Safari 16.2+ 支持，
 * 不支持的旧浏览器会忽略该声明，退回透明底 + 强调色字，仍可读。
 */
const resolveChipStyle = (accent: string | undefined, isDigitalHuman?: boolean): React.CSSProperties => {
  if (isDigitalHuman) {
    return {
      '--file-chip-color': '#ffffff',
      '--file-chip-bg': 'rgba(255, 255, 255, 0.18)',
      '--file-chip-bg-hover': 'rgba(255, 255, 255, 0.3)',
    } as React.CSSProperties;
  }

  if (!accent) return {};

  return {
    '--file-chip-color': accent,
    '--file-chip-bg': `color-mix(in srgb, ${accent} 12%, transparent)`,
    '--file-chip-bg-hover': `color-mix(in srgb, ${accent} 20%, transparent)`,
  } as React.CSSProperties;
};

const toText = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toText).join('');
  if (React.isValidElement(node)) return toText((node.props as { children?: React.ReactNode }).children);
  return '';
};

export const FileChip = ({
  href,
  title,
  children,
  referenceList,
  accentColor,
  textColor,
  bubbleColor,
  isDigitalHuman,
}: FileChipProps) => {
  const { pageSource } = useChatStore();
  const theme = useMantineTheme();
  const openViewer = useFileViewerStore((s) => s.open);
  const clearCitation = useFilePreviewStore((s) => s.clearReferenceInfo);
  const isWidget = pageSource === 'widget';

  // 主题主色的实际色值（与 --mantine-primary-color-light-color 同一档），用于和自定义气泡色比对比度
  const primaryShade = typeof theme.primaryShade === 'number' ? theme.primaryShade : theme.primaryShade.light;
  const themeColor = theme.colors[theme.primaryColor]?.[primaryShade];
  const chipStyle = resolveChipStyle(
    pickReadableAccent({ accentColor, textColor, bubbleColor, themeColor }),
    isDigitalHuman
  );

  const link = parseFilePreviewLink(href);
  const referenceName = link ? referenceList?.find((item) => item.file_id === link.fileId)?.title : undefined;
  const linkText = toText(children);

  // 文件名优先级：title 位 → references 文件名 → 链接文字（仅当它看起来像文件名）
  const fileName = title?.trim() || referenceName?.trim() || (resolveFileChipKind(linkText) ? linkText : '');

  // 类型判定顺序（与后端约定一致）：title 后缀 → ?type= → references 文件名 → 链接文字后缀 → 通用
  const kind: FileChipKind =
    resolveFileChipKind(title) ||
    resolveFileChipKind(link?.type ? `x.${link.type}` : '') ||
    resolveFileChipKind(referenceName) ||
    resolveFileChipKind(linkText) ||
    'file';

  const Icon = KIND_ICON[kind];

  const openHtml = (fileId: string) => {
    // 必须在点击的同步调用栈里先开窗，否则等 blob 回来再 open 会被浏览器拦截
    const target = window.open('', '_blank');
    if (!target) return;
    Http.use()
      .get<Blob>(`/embeddings/files/${fileId}/download`, { responseType: 'blob' })
      .then((response) => {
        target.location.href = window.URL.createObjectURL(response.data);
      })
      .catch((error) => {
        target.close();
        // 接口失败的用户提示由 http 层拦截器统一弹 toast，这里只留日志，避免重复提示
        console.error('文件卡片：网页加载失败', error);
      });
  };

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    if (!link) return;
    const { fileId, page } = link;

    if (kind === 'web') {
      openHtml(fileId);
      return;
    }
    if (isWidget) {
      openFilePreviewTab({ kind: 'file', file_id: fileId, page, title: fileName });
      return;
    }
    // 两个预览面板共用右侧位置：先收起角标的 PDFViewer
    clearCitation();
    openViewer({ file_id: fileId, fileName, page });
  };

  return (
    <Tooltip label={fileName} withArrow disabled={!fileName}>
      <a href={href} className={classes.fileChip} style={chipStyle} onClick={handleClick}>
        <Icon size={14} />
        <span className={classes.fileChipText}>{linkText || fileName || href}</span>
      </a>
    </Tooltip>
  );
};
